import { useState, useEffect } from "react";
import { JobPost } from "@/types/jobPost";
import axiosInstance, { PaginationResponse } from "@/hooks/axios";
import { isAxiosError } from "axios";
import {
  JobPostFiltersState,
  MAX_SALARY,
  MIN_SALARY,
} from "@/providers/contexts/FilterJobsContext";
import * as Sentry from "@sentry/react";

export const POSTS_PER_PAGE = 10;

export interface JobPostFilterResponse {
  jobPostValues: JobPost[];
  filterValues: JobPostFiltersState;
}

export interface HookResponse extends PaginationResponse {
  results: JobPostFilterResponse;
}

export interface PaginationState extends PaginationResponse {
  totalPages: number;
}
export interface UseJobPostOptions {
  filters?: JobPostFiltersState;
  skip?: boolean;
  page?: number;
}

export const defaultJobPostsFilters: JobPostFiltersState = {
  selectedCompanies: [],
  selectedHealthcareDomains: [],
  selectedLocations: [],
  selectedCompanySizes: [],
  selectedDepartments: [],
  selectedJobRoles: [],
  selectedJobSettings: [],
  selectedJobSalaryCurrencies: [],
  selectedSalaryRangeMin: MIN_SALARY,
  selectedSalaryRangeMax: MAX_SALARY,
  selectedDatePostedOption: null,
  selectedDateUpdatedOption: null,
  search: "",
};

// map params from FE to match BE, but want to stay consistent with camelCase
const queryParamMap: Record<string, string> = {
  selectedCompanies: "companies",
  selectedHealthcareDomains: "healthcare_domains",
  selectedLocations: "locations",
  selectedCompanySizes: "company_sizes",
  selectedDepartments: "departments",
  selectedJobRoles: "job_roles",
  selectedJobSettings: "job_settings",
  selectedJobSalaryCurrencies: "job_salary_currencies",
  selectedSalaryRangeMin: "salary_range_min",
  selectedSalaryRangeMax: "salary_range_max",
  selectedDatePostedOption: "date_posted",
  selectedDateUpdatedOption: "date_updated",
  search: "search",
};

export function parseJobPostDates(jobPost: JobPost): JobPost {
  return {
    ...jobPost,
    created_at: new Date(jobPost.created_at),
    updated_at: new Date(jobPost.updated_at),
    job_posted: jobPost.job_posted ? new Date(jobPost.job_posted) : null,
    job_updated: jobPost.job_updated ? new Date(jobPost.job_updated) : null,
  };
}

export const useJobPosts = ({
  filters = defaultJobPostsFilters,
  skip = false,
  page = 1,
}: UseJobPostOptions = {}) => {
  const [jobPosts, setJobPosts] = useState<JobPost[]>([]);

  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    totalPages: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip) {
      return;
    }

    const fetchJobPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        const queryParams = Object.entries(filters).reduce(
          (acc, [key, value]) => {
            const backendKey = queryParamMap[key] ?? key;

            if (
              value !== undefined &&
              value !== null &&
              !(Array.isArray(value) && value.length === 0) &&
              value !== ""
            ) {
              if (
                key === "selectedDatePostedOption" ||
                key === "selectedDateUpdatedOption"
              ) {
                acc[backendKey] = (value as { value: number }).value.toString();
              } else if (Array.isArray(value)) {
                if (
                  value.length &&
                  typeof value[0] === "object" &&
                  "id" in value[0]
                ) {
                  acc[backendKey] = value.map(v => v.id).join(",");
                } else {
                  acc[backendKey] = value.join(",");
                }
              } else {
                acc[backendKey] = value.toString();
              }
            }

            return acc;
          },
          {} as Record<string, string>,
        );

        queryParams.page = page.toString();
        const queryString = new URLSearchParams(queryParams).toString();
        const url = `jobs/job_posts/?${queryString}`;

        const response = await axiosInstance.get<HookResponse>(url);

        if (
          !response.data.results ||
          !response.data.results.jobPostValues ||
          !response.data.results.filterValues
        ) {
          console.error("Invalid job posts API response:", response.data);
          setError("Failed to load job posts. Please try again later.");
          setLoading(false);
          return;
        }

        const results = response.data.results;

        const parsedJobPosts = results.jobPostValues.map(parseJobPostDates);

        setJobPosts(parsedJobPosts);

        setPagination({
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          totalPages: Math.ceil(response.data.count / POSTS_PER_PAGE),
        });
      } catch (err: unknown) {
        if (isAxiosError(err)) {
          Sentry.captureException(err, {
            contexts: {
              axiosResponse: {
                url: err.config?.url,
                method: err.config?.method,
                status: err.response?.status,
                data: err.response?.data,
              },
            },
          });
          setError(err.message);
        } else if (err instanceof Error) {
          Sentry.captureException(err);
          setError(err.message);
        } else {
          Sentry.captureException(new Error("Unknown error occurred"));
          setError("An unknown error occurred.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJobPosts();
  }, [filters, skip, page]);

  return {
    jobPosts,
    loading,
    error,
    pagination,
  };
};
